/**
 * Libellés d'interface — version française.
 *
 * Ce fichier est la SOURCE DE VERITE des clés : `en.ts` est typé d'après lui
 * (`typeof fr`), donc une clé ajoutée ici et oubliée là-bas fait echouer
 * `npm run typecheck`. Ne contient que des libellés d'interface ; le contenu
 * editorial vit dans `src/data/fr/`.
 */
export const fr = {
  nav: {
    ariaLabel:     'Navigation principale',
    home:          'Accueil — Pierre Séré',
    expertise:     'Expertise',
    cnc:           'CNC Tracy',
    asterion:      'Asterion',
    projects:      'Projets',
    articles:      'Articles',
    skills:        'Stack',
    timeline:      'Parcours',
    contact:       'Contact',
    founder:       'Le fondateur',
    toLight:       'Passer en mode clair',
    toDark:        'Passer en mode sombre',
    switchLang:    'Read this page in English',
  },

  hero: {
    location:      'Sorel-Tracy, Québec · Canada',
    typed: [
      'Technicien en Génie Mécanique',
      'Inspecteur Qualité · ISO 9001 · GMAO',
      'Inspection Dimensionnelle · GD&T · NDT',
      'Gestion de Projet · Amélioration Continue',
      'Fondateur Asterion Solutions',
      'Lauréat OSEntreprendre 2026',
    ],
    bio:
      "Technicien en génie mécanique à Sorel-Tracy (Montérégie), je transforme " +
      "les opérations manufacturières en systèmes plus simples, plus rapides et " +
      "plus fiables. Pas un développeur — un inspecteur qualité de terrain " +
      "qui bâtit les outils et les méthodes que l'industrie n'avait pas encore.",
    yearsLabel:    'ans de métier',
    reportsValue:  '1000+',
    reportsLabel:  'rapports générés avec Asterion',
    ctaContact:    'Me contacter',
    ctaJourney:    'Voir le parcours',
    scroll:        'Scroll',
    scrollAria:    "Faire défiler vers l'expertise",
  },

  photo: {
    stamp:         'INSPECTÉ',
    award:         'OSEntreprendre · 2026',
    refLabel:      'REF',
    expLabel:      'EXP',
    expValue:      '14 ANS',
    statusLabel:   'STATUT',
    statusValue:   'ACTIF',
    alt:           'Pierre Séré, technicien en génie mécanique et fondateur d’Asterion Solutions',
  },

  intro: {
    tagline:       'Génie Mécanique · Fondateur Asterion Solutions · Lauréat OSEntreprendre 2026',
    role:          'Technicien · Bâtisseur',
    enter:         'Entrée',
    press:         'Appuyez sur',
  },

  sections: {
    expertise: {
      chip:        'Expertise',
      title:       'Terrain &\nPrécision',
      subtitle:    '14 ans de génie mécanique industriel au Québec — inspection dimensionnelle, GD&T, NDT, systèmes qualité ISO 9001.',
    },
    cnc: {
      chip:        'CNC Tracy',
      title:       'En Production\nChez CNC Tracy',
      subtitle:    "Hub d'applications interne sécurisé — authentifié Microsoft 365, toutes intégrées à l'ERP Notixia, en production depuis 2023.",
      masterpiece: "Chef-d'œuvre",
    },
    asterion: {
      chip:        'Asterion',
      title:       "L'Écosystème\nAsterion",
      subtitle:    "Suite d'applications SaaS B2B industrielles — bâtie de zéro, en production.",
      cta:         'Voir Asterion Solutions ↗',
      ctaSub:      'SaaS B2B industriel · Multi-tenant · En production',
      comingSoon:  'Bientôt',
      shotAlt:     "Capture d'écran de",
      enlarge:     'Agrandir la capture',
      close:       'Fermer',
    },
    projects: {
      chip:        'Projets',
      title:       'Projets\nPersonnels',
      subtitle:    'Des prototypes, des expérimentations, des solutions à des problèmes réels.',
      visit:       'Visiter',
    },
    articles: {
      chip:        'Articles',
      title:       'Mes\nArticles',
      subtitle:    "J'écris sur l'intelligence artificielle appliquée à la qualité industrielle dans les PME manufacturières. Des articles de fond, aussi publiés sur le blog d'Asterion Solutions.",
      readMore:    "Lire l'article →",
    },
    skills: {
      chip:        'Profil',
      title:       'Compétences\n& Outils',
      subtitle:    'Expertise terrain de 14 ans + IA appliquée. Pas des démos — des outils industriels en production.',
    },
    timeline: {
      chip:        'Parcours',
      title:       'Chronologie\nProfessionnelle',
      subtitle:    "De machiniste CNC à Sorel-Tracy jusqu'à la startup — chaque étape a construit la suivante.",
    },
    contact: {
      chip:        'Contact',
      title:       'Discutons\nEnsemble',
      subtitle:    "Un emploi, un défi, du freelance ou Asterion Solutions — si l'une de ces situations vous parle, la conversation vaut la peine.",
      available:   'Ouvert aux opportunités',
    },
    pillars: {
      heading:     'Trois leviers, un objectif : une organisation plus performante',
      title:       "Ce que j'apporte",
    },
    rupture: {
      chip:        'Ce qui me rend différent',
    },
  },

  articles: {
    portfolio:     '← Portfolio',
    indexKicker:   'Le blog',
    indexTitle:    'Qualité, IA & PME manufacturières',
    indexIntro:    "Des articles de fond, écrits du terrain, sur ce que l'intelligence artificielle change — et ne change pas — pour les ateliers qui veulent structurer leur qualité.",
    indexCount:    "dossiers d'expertise",
    exploreByTheme:'Explorer par thème',
    indexBack:     '← Retour à l’accueil',
    backToIndex:   '← Tous les articles',
    by:            'Par',
    readingTime:   'min de lecture',
    tocTitle:      'Sommaire',
    readOnAsterion:'Lire sur le blog Asterion →',
    frenchOnly:    'Article disponible en français',
    browseAll:     'Parcourir tous les articles →',
    seeOnAsterion: 'Voir sur Asterion ↗',
    blogUrl:       'https://solutions-asterion.com/blog',
  },

  founder: {
    intro:       "Comment un technicien qualité s'est mis à coder pour son propre atelier, et comment c'est devenu une entreprise.",
    ctaText:     "Le catalogue des applications, les tarifs et les modalités sont sur le site de l'entreprise.",
    ctaLink:     'Voir Asterion Solutions ↗',
    relatedAria: 'Pages liées',
    toTimeline:  'Le parcours détaillé de Pierre Séré →',
    toArticles:  'Les articles de fond →',
  },

  footer: {
    rights:        'Tous droits réservés',
    builtWith:     'Bâti avec React, TypeScript et Firebase',
  },
};

/**
 * Forme attendue de tout dictionnaire de langue.
 *
 * Volontairement SANS `as const` sur `fr` : figer les valeurs en types litteraux
 * obligerait `en.ts` a repeter le texte francais mot pour mot pour compiler.
 * Ici, seules les CLES et leurs TYPES sont imposes.
 */
export type UIStrings = typeof fr;
